import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { UpdateSeoConfigRequest } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useSeoConfig = () => {
	const { id: companyId } = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Seo.Config(companyId),
		queryFn: async () => (await Api.GetSeoConfig()).data,
	}).data;
};

const defaultSaveErrorMessage =
	"Failed to save your SEO profile. Please try again later.";
const defaultSeedErrorMessage =
	"Failed to generate your SEO profile. Please try again later.";

export const useSeoConfigActions = () => {
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const { id: companyId } = useCompany();

	const save = useMutation({
		mutationFn: async (dto: UpdateSeoConfigRequest) =>
			(await Api.UpdateSeoConfig(dto)).data,
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Seo.Config(companyId),
			});

			emit({
				type: AlertType.SUCCESS,
				content: "Your SEO profile has been saved.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data?.message || defaultSaveErrorMessage,
				});

				return;
			}

			emit({
				type: AlertType.ERROR,
				content: defaultSaveErrorMessage,
			});
		},
	});

	const seed = useMutation({
		mutationFn: async () => (await Api.SeedSeoConfig()).data,
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Seo.Config(companyId),
			});

			emit({
				type: AlertType.SUCCESS,
				content: "Your SEO profile has been generated.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data?.message || defaultSeedErrorMessage,
				});

				return;
			}

			emit({
				type: AlertType.ERROR,
				content: defaultSeedErrorMessage,
			});
		},
	});

	return { save, seed };
};
